'use client'

import { Button } from '@ui/base/button'
import { Checkbox } from '@ui/base/checkbox'
import { Field, FieldError, FieldLabel, FieldTitle } from '@ui/base/field'

import { type Item } from '../api/types'

type DistrictCheckboxListProps = {
  districts: readonly Item[] | undefined
  selectedCodes: readonly string[]
  onToggle: (district: Item, checked: boolean) => void
  isLoading: boolean
  hasLoadError: boolean
  onRetry: () => void
  labelId: string
  errorDescriptionId: string
}

const LoadingNotice = () => <p role="status">Загружаем федеральные округа…</p>

const LoadErrorNotice = ({
  errorDescriptionId,
  onRetry,
}: Pick<DistrictCheckboxListProps, 'errorDescriptionId'> & { onRetry: () => void }) => (
  <div className="grid justify-items-start gap-3">
    <FieldError id={errorDescriptionId}>Не удалось загрузить федеральные округа. Попробуйте ещё раз.</FieldError>
    <Button type="button" variant="outline" onClick={onRetry}>
      Повторить загрузку
    </Button>
  </div>
)

/**
 * Перечень округов флажками в два столбца — общий для заявки продавца и карточки фулфилмента.
 * Сам ничего не загружает: перечень и состояния показа приходят пропсами, обычно из
 * `useFederalDistricts`. Подписи `labelId` и `errorDescriptionId` задаёт вызывающая сторона —
 * у каждой формы своя надпись над перечнем и свой текст ошибки.
 */
export const DistrictCheckboxList = ({
  districts,
  selectedCodes,
  onToggle,
  isLoading,
  hasLoadError,
  onRetry,
  labelId,
  errorDescriptionId,
}: DistrictCheckboxListProps) => {
  const content = () => {
    if (isLoading) {
      return <LoadingNotice />
    }
    if (hasLoadError) {
      return <LoadErrorNotice errorDescriptionId={errorDescriptionId} onRetry={onRetry} />
    }

    return (
      // Округа встают в два столбца: восьмёрка в один столбец растягивает шаг на экран.
      <div className="grid gap-3 sm:grid-cols-2">
        {(districts ?? []).map((district) => {
          const { code, name } = district
          const id = `${labelId}-district-${code}`

          return (
            <FieldLabel key={code} htmlFor={id}>
              <Field orientation="horizontal">
                <Checkbox
                  id={id}
                  size="lg"
                  aria-labelledby={`${id}-title`}
                  checked={selectedCodes.includes(code)}
                  onCheckedChange={(checked) => onToggle(district, checked === true)}
                />
                <FieldTitle id={`${id}-title`}>{name}</FieldTitle>
              </Field>
            </FieldLabel>
          )
        })}
      </div>
    )
  }

  return (
    <Field
      aria-labelledby={labelId}
      aria-describedby={hasLoadError ? errorDescriptionId : undefined}
      aria-invalid={hasLoadError}
      data-invalid={hasLoadError}
    >
      {content()}
    </Field>
  )
}
